import Storage from "./Storage";

export const UTM_PARAMS_STORAGE_KEY = "utmParams";

const TRACKED_PARAMS = [
    "utm_campaign",
    "utm_content",
    "utm_id",
    "utm_medium",
    "utm_source",
    "utm_term",
    "thm",
    ...Array.from({ length: 10 }, (_, index) => `aff_sub${index}`),
];

const THIRTY_DAYS_IN_SECONDS = 60 * 60 * 24 * 30;

/**
 * The SPA equivalent of CookieSettingsAndAnalyticsMiddleware::saveUtmParamsToSession.
 * On the turbo host first-party navigations never reach Laravel, so that middleware
 * never runs. Landing-page attribution params are persisted here and replayed to the
 * backend inside the register POST body (and on the Google signup redirect), where
 * the middleware picks them up into the session.
 */
export function captureUtmParams() {
    const queryParams = new URLSearchParams(globalThis.location.search);
    const storedParams = loadUtmParams();
    let hasNewValues = false;

    for (const param of TRACKED_PARAMS) {
        const value = queryParams.get(param);
        if (value !== null) {
            storedParams[param] = value;
            hasNewValues = true;
        }
    }

    if (hasNewValues) {
        Storage.save(UTM_PARAMS_STORAGE_KEY, storedParams, {
            secondsTillExpiry: THIRTY_DAYS_IN_SECONDS,
        });
    }
}

export function loadUtmParams(): Record<string, string> {
    return Storage.load<Record<string, string>>(UTM_PARAMS_STORAGE_KEY, {});
}

/**
 * Puts the persisted landing params on a URL. Needed for top-level navigations
 * to Laravel — the Google signup handshake — where the query string is the only
 * way the session learns about them.
 */
export function withUtmParams(url: string): string {
    const target = new URL(url, globalThis.location.origin);

    for (const [key, value] of Object.entries(loadUtmParams())) {
        target.searchParams.set(key, value);
    }

    return target.toString();
}

/**
 * The SPA equivalent of Laravel's session('thm') campaign theme check:
 * the current URL wins, falling back to the persisted landing params.
 */
export function loadThm(): string | null {
    const urlThm = new URLSearchParams(globalThis.location.search).get("thm");
    const thm = urlThm ?? loadUtmParams().thm;
    return thm ? thm.toLowerCase() : null;
}
